import * as Tabs from "@radix-ui/react-tabs";
import {
  ArrowRight,
  CheckCircle2,
  Database,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  Orbit,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useState, type FormEvent } from "react";
import { Navigate, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { BlurText } from "../components/BlurText";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { Badge, Button, Card, Input } from "../components/ui";
import { ApiRequestError, USE_DEMO_DATA, authApi } from "../lib/api";

const Aurora = lazy(() =>
  import("../components/react-bits/Aurora").then((module) => ({ default: module.Aurora }))
);

type Mode = "login" | "signup" | "forgot" | "reset";

export function LoginPage() {
  const { session, loading, login, signup, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState(USE_DEMO_DATA ? "demo@solab.quocminh.org" : "");
  const [password, setPassword] = useState(USE_DEMO_DATA ? "DemoPass2026!" : "");
  const [displayName, setDisplayName] = useState("");
  const [adult, setAdult] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const onTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);

  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const token = fragment.get("access_token");
    if (fragment.get("type") === "recovery" && token) {
      setRecoveryToken(token);
      setMode("reset");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  if (!loading && session) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      if (mode === "reset") {
        const result = await authApi.resetPassword(password, recoveryToken);
        setMessage(result.message);
        setMode("login");
        return;
      }
      if (!turnstileToken) throw new Error("Vui lòng hoàn tất xác minh chống bot.");
      if (mode === "forgot") {
        setMessage(await forgotPassword(email, turnstileToken));
        return;
      }
      if (mode === "signup") {
        if (!adult) throw new Error("Bạn cần xác nhận đã đủ 18 tuổi.");
        const result = await signup({
          email,
          password,
          displayName,
          adultAcknowledged: adult,
          turnstileToken
        });
        if (USE_DEMO_DATA) navigate("/dashboard");
        else {
          setMessage(result);
          setMode("login");
        }
        return;
      }
      await login({ email, password, turnstileToken });
      navigate("/dashboard");
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError || caught instanceof Error
          ? caught.message
          : "Không thể hoàn tất yêu cầu."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const heading =
    mode === "forgot"
      ? "Khôi phục tài khoản"
      : mode === "reset"
        ? "Đặt mật khẩu mới"
        : mode === "signup"
          ? "Tạo trạm quan sát"
          : "Chào mừng trở lại";

  return (
    <main className="aurora-fallback relative min-h-screen overflow-hidden bg-background p-3 sm:p-6 lg:p-8">
      <div className="pointer-events-none absolute inset-0 opacity-75">
        <Suspense fallback={null}><Aurora /></Suspense>
      </div>
      <div className="number-grid-bg pointer-events-none absolute inset-0 opacity-35" />

      <div className="relative mx-auto grid min-h-[calc(100vh-1.5rem)] max-w-[1480px] overflow-hidden rounded-[2rem] border border-white/10 bg-[#071a17]/70 shadow-2xl backdrop-blur-xl sm:min-h-[calc(100vh-3rem)] lg:grid-cols-[1.08fr_0.92fr]">
        <section className="relative hidden min-h-[720px] flex-col overflow-hidden p-10 text-white lg:flex xl:p-14">
          <div className="signal-sweep pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 opacity-60" aria-hidden="true" />
          <div className="flex items-center gap-3">
            <span className="flex size-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10">
              <img src="/brand-mark.svg" alt="" className="size-8" />
            </span>
            <div>
              <strong className="block text-base font-extrabold">LottoLab VN</strong>
              <span className="text-[9px] font-black uppercase tracking-[0.22em] text-teal-200">
                Data observatory
              </span>
            </div>
          </div>

          <div className="my-auto max-w-[42rem]">
            <Badge className="mb-7 border-teal-300/20 bg-teal-300/10 text-teal-100">
              <Orbit className="size-3.5" /> Trạm phân tích đang hoạt động
            </Badge>
            <h1 className="sr-only">Quan sát tín hiệu. Kiểm chứng dữ liệu.</h1>
            <BlurText
              text="Quan sát tín hiệu. Kiểm chứng dữ liệu."
              delay={34}
              aria-hidden
              className="max-w-2xl text-5xl font-extrabold leading-[1.02] tracking-[-0.055em] xl:text-[4.35rem]"
            />
            <p className="mt-6 max-w-xl text-base leading-7 text-emerald-50/70">
              Một không gian minh bạch để theo dõi mô hình, đối chiếu kết quả và lưu
              những bộ số bạn muốn quan sát qua từng kỳ quay.
            </p>

            <div className="mt-10 grid max-w-2xl grid-cols-3 gap-3">
              {[
                { value: "03", label: "sản phẩm", icon: Orbit },
                { value: "05", label: "bộ mỗi kỳ", icon: Sparkles },
                { value: "100%", label: "có nguồn", icon: Database }
              ].map((item) => (
                <div key={item.label} className="rounded-[1.35rem] border border-white/10 bg-white/[0.06] p-4 backdrop-blur-xl">
                  <item.icon className="mb-6 size-[18px] text-teal-200" />
                  <strong className="mono-data block text-2xl font-black">{item.value}</strong>
                  <span className="mt-1 block text-[10px] text-white/45">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex max-w-2xl items-start gap-2.5 border-t border-white/10 pt-5 text-[10px] leading-4 text-white/45">
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-teal-200" />
            LottoLab VN không liên kết hoặc được Vietlott bảo trợ. Xổ số hoàn toàn ngẫu nhiên.
          </div>
        </section>

        <section className="relative flex min-h-[calc(100vh-1.5rem)] items-center justify-center bg-background/94 p-4 sm:p-8 lg:min-h-0 lg:p-10">
          <div className="data-grid-bg pointer-events-none absolute inset-0 opacity-45 dark:opacity-20" />
          <div className="relative w-full max-w-[490px]">
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <span className="flex size-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10">
                <img src="/brand-mark.svg" alt="" className="size-7" />
              </span>
              <div>
                <strong className="block font-extrabold">LottoLab VN</strong>
                <span className="text-[8px] font-black uppercase tracking-[0.2em] text-primary">Data observatory</span>
              </div>
            </div>

            <Badge className="mb-4 border-primary/20 bg-primary/10 text-primary">
              <span className="status-pulse size-1.5 rounded-full bg-primary" /> Hệ thống sẵn sàng
            </Badge>
            <h2 className="text-3xl font-extrabold tracking-[-0.045em] md:text-4xl">{heading}</h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              {mode === "forgot"
                ? "Nhập email để nhận liên kết đặt lại mật khẩu."
                : mode === "reset"
                  ? "Chọn mật khẩu mới có ít nhất 10 ký tự."
                  : mode === "signup"
                    ? "Tạo hồ sơ để lưu và đối chiếu tín hiệu của riêng bạn."
                    : "Đăng nhập để tiếp tục phiên phân tích của bạn."}
            </p>

            {mode !== "forgot" && mode !== "reset" ? (
              <Tabs.Root value={mode} onValueChange={(value) => setMode(value as Mode)} className="mt-6">
                <Tabs.List className="grid grid-cols-2 rounded-[1.1rem] border border-border/70 bg-muted/70 p-1" aria-label="Chọn loại biểu mẫu">
                  {(["login", "signup"] as const).map((value) => (
                    <Tabs.Trigger
                      key={value}
                      value={value}
                      className="min-h-10 rounded-[13px] text-sm font-extrabold text-muted-foreground outline-none transition-colors data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {value === "login" ? "Đăng nhập" : "Tạo tài khoản"}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
              </Tabs.Root>
            ) : null}

            <Card className="mt-5 p-5 sm:p-7">
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === "signup" ? (
                  <label className="block">
                    <span className="mb-2 block text-xs font-extrabold">Tên hiển thị</span>
                    <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" placeholder="Tên của bạn" maxLength={80} required />
                  </label>
                ) : null}

                {mode !== "reset" ? (
                  <label className="block">
                    <span className="mb-2 block text-xs font-extrabold">Email</span>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-3.5 size-5 text-muted-foreground" />
                      <Input className="pl-10" value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" placeholder="ban@example.com" required />
                    </div>
                  </label>
                ) : null}

                {mode !== "forgot" ? (
                  <label className="block">
                    <span className="mb-2 block text-xs font-extrabold">Mật khẩu</span>
                    <div className="relative">
                      <LockKeyhole className="pointer-events-none absolute left-3 top-3.5 size-5 text-muted-foreground" />
                      <Input className="px-10" value={password} onChange={(event) => setPassword(event.target.value)} type={showPassword ? "text" : "password"} autoComplete={mode === "signup" ? "new-password" : "current-password"} placeholder="Tối thiểu 10 ký tự" minLength={10} required />
                      <button type="button" className="absolute right-1 top-0 flex size-12 items-center justify-center rounded-xl text-muted-foreground hover:text-foreground" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}>
                        {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                      </button>
                    </div>
                  </label>
                ) : null}

                {mode === "signup" ? (
                  <label className="flex items-start gap-3 rounded-2xl border border-border/70 bg-muted/50 p-3.5 text-sm">
                  <input type="checkbox" className="mt-0.5 size-5 accent-teal-600" checked={adult} onChange={(event) => setAdult(event.target.checked)} required />
                    <span className="leading-5 text-muted-foreground">
                      Tôi xác nhận đã đủ 18 tuổi và đồng ý với <NavLink to="/terms" className="font-bold text-primary hover:underline">điều khoản</NavLink> cùng <NavLink to="/privacy" className="font-bold text-primary hover:underline">chính sách riêng tư</NavLink>.
                    </span>
                  </label>
                ) : null}

                {mode !== "reset" ? <TurnstileWidget onToken={onTurnstileToken} /> : null}

                <div aria-live="polite" aria-atomic="true">
                  {error ? <p role="alert" className="rounded-2xl bg-destructive/10 p-3 text-sm font-semibold text-destructive">{error}</p> : null}
                  {message ? <p className="flex items-start gap-2 rounded-2xl bg-primary/10 p-3 text-sm font-semibold text-primary"><CheckCircle2 className="mt-0.5 size-4 shrink-0" />{message}</p> : null}
                </div>

                <Button className="w-full" type="submit" disabled={submitting}>
                  {submitting ? "Đang xử lý…" : mode === "login" ? "Đăng nhập" : mode === "signup" ? "Tạo tài khoản" : mode === "reset" ? "Đổi mật khẩu" : "Gửi liên kết khôi phục"}
                  {!submitting ? <ArrowRight className="size-4" /> : null}
                </Button>

                {mode !== "reset" ? (
                  <button type="button" className="mx-auto flex min-h-10 items-center justify-center text-sm font-bold text-primary hover:underline" onClick={() => setMode(mode === "forgot" ? "login" : "forgot")}>
                    {mode === "forgot" ? "Quay lại đăng nhập" : "Quên mật khẩu?"}
                  </button>
                ) : null}
              </form>
            </Card>
            <p className="mt-5 text-center text-[11px] leading-5 text-muted-foreground">
              Không phải lời khuyên tài chính. Không có phương pháp nào bảo đảm trúng thưởng.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
